import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getOrderProductResolution, getOrderSubsourceResolution } from '@/api/orders';
import { useOrderWorkspaceSession } from '@/hooks/useOrderWorkspaceSession';

export const BRIEF_REFERENCE_TIMEOUT = 15000;

const record = value => !!value && typeof value === 'object' && !Array.isArray(value);
const optionalText = value => value == null || typeof value === 'string';
const validResolution = (value, kind) => {
  if (!record(value) || typeof value.resolved !== 'boolean') return false;
  if (!value.resolved) return true;
  if (kind === 'florist') return record(value.sending_florist)
    && optionalText(value.sending_florist.name) && optionalText(value.sending_florist.product_url);
  return record(value.product) && typeof value.product.id === 'string'
    && optionalText(value.product.product_name) && optionalText(value.size_label)
    && (value.variant == null || (record(value.variant) && optionalText(value.variant.recipe_text)));
};

// Reference observations have their own lifetime. Retiring these reads must not
// remount the brief's independently owned notes, arrangement photos or printing.
export function useDesignerBriefReferences(orderId) {
  const session = useOrderWorkspaceSession();
  const sessionRef = useRef(session); sessionRef.current = session;
  const owner = useMemo(() => ({ orderId, sessionKey: session.key, active: false, request: null }), [orderId, session.key]);
  const latest = useRef(owner); latest.current = owner;
  owner.current = () => owner.active && latest.current === owner && !!owner.request?.current();
  const [observed, setObserved] = useState(null);
  useLayoutEffect(() => {
    owner.active = true;
    owner.request = orderId ? sessionRef.current.capture() : null;
    return () => { owner.active = false; };
  }, [owner, orderId]);

  useEffect(() => {
    let retired = false;
    const disposers = [];
    const owns = () => !retired && owner.current();
    const publish = (kind, state, value = null) => {
      if (!owns()) return;
      setObserved(previous => !owns() ? previous : ({
        ...(previous?.owner === owner ? previous : {}),
        owner, [kind]: { state, value },
      }));
    };
    const read = (kind, resolve) => {
      const controller = new AbortController();
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        publish(kind, 'unavailable');
        controller.abort();
      }, BRIEF_REFERENCE_TIMEOUT);
      disposers.push(() => { settled = true; clearTimeout(timer); controller.abort(); });
      const finish = value => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        const valid = validResolution(value, kind);
        publish(kind, valid ? 'ready' : 'unavailable', valid ? value : null);
      };
      Promise.resolve().then(() => {
        if (!owns() || settled) return null;
        return resolve(owner.orderId, {
          ...owner.request.config, signal: controller.signal, timeout: BRIEF_REFERENCE_TIMEOUT,
        });
      }).then(finish, () => finish(null));
    };
    if (owner.current()) {
      read('product', getOrderProductResolution);
      read('florist', getOrderSubsourceResolution);
    }
    return () => { retired = true; disposers.forEach(dispose => dispose()); };
  }, [owner]);

  const accepted = owner.current() && observed?.owner === owner ? observed : null;
  const fallback = orderId && session.capture() ? 'loading' : 'unavailable';
  const productState = accepted?.product?.state || fallback;
  const floristState = accepted?.florist?.state || fallback;
  const mediaOwner = useMemo(() => ({
    current: () => owner.current(),
    headers: () => owner.request?.config.headers,
    timeoutMs: BRIEF_REFERENCE_TIMEOUT,
  }), [owner]);
  return {
    product: accepted?.product?.value || null,
    florist: accepted?.florist?.value || null,
    productState, floristState, mediaOwner,
  };
}
